import { Injectable } from "@angular/core";
import {
  AvcoreSubcribeStreamApiCredentials,
  RoomContextId,
  StreamId,
  StreamKind
} from "@common/domain";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { LokiActions } from "@youm-front/loki/data-access";
import { ErrorHandlerService } from "@youm-front/utils/error-handler";
import { tap } from "rxjs";
import {
  isPermanentSubscribeError,
  RemoteConferenceStream,
  RemoteConferenceStreamManager
} from "youm-lib/conference";
import { RemoteConferenceStreamActions } from "../actions";
import { RemoteConferenceStreamsListService } from "../service";

interface RemoteConferenceStreamRequest {
  id: StreamId;
  kinds: StreamKind[];
  credentials: AvcoreSubcribeStreamApiCredentials;
  roomContextId: RoomContextId;
}

@Injectable()
export class RemoteConferenceStreamEffects {
  // Single in-flight subscription per stream id, bounded retry of a failed
  // subscribe, cancellation on delete/dispose; a stream is listed only once
  // it is subscribed.
  private readonly _manager = new RemoteConferenceStreamManager<
    RemoteConferenceStream,
    RemoteConferenceStreamRequest
  >({
    create: (_) => this.create(_),
    onAdd: (_) => this.remoteConferenceStreamsListService.add(_),
    onRemove: (_) => this.remoteConferenceStreamsListService.remove(_),
    isPermanentError: isPermanentSubscribeError,
    onError: (error, { streamId, attempt, willRetry }) => {
      if (willRetry) {
        this.log([
          "RemoteConferenceStream subscribe retry",
          {
            streamId,
            attempt,
            error: error instanceof Error ? error.message : error
          }
        ]);
        return;
      }
      this.errorHandlerService.error(error);
      // attempt 0: a failed destroy, not a subscribe.
      if (attempt > 0) {
        this.store.dispatch(
          RemoteConferenceStreamActions.streamSubscribeFailure({ id: streamId })
        );
      }
    }
  });

  constructor(
    private readonly actions$: Actions,
    private readonly remoteConferenceStreamsListService: RemoteConferenceStreamsListService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly store: Store
  ) {}

  streamAdd$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteConferenceStreamActions.streamAdd),
        tap((_) => this._manager.add(_.id, _))
      ),
    { dispatch: false }
  );

  streamDelete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteConferenceStreamActions.streamDelete),
        tap((_) => this._manager.delete(_.id))
      ),
    { dispatch: false }
  );

  reconcile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteConferenceStreamActions.reconcile),
        tap((_) =>
          this._manager.reconcile(
            new Map(_.streams.map((stream) => [stream.id, stream])),
            _.requestedAt
          )
        )
      ),
    { dispatch: false }
  );

  // Also cancels subscriptions that are still in flight or waiting for a retry.
  dispose$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteConferenceStreamActions.dispose),
        tap(() => this._manager.dispose())
      ),
    { dispatch: false }
  );

  private create(request: RemoteConferenceStreamRequest) {
    return new RemoteConferenceStream(
      {
        streamId: request.id,
        kinds: request.kinds
      },
      request.credentials,
      (...args: unknown[]) => this.log(args, request.roomContextId)
    );
  }

  private log(args: unknown[], roomContextId?: RoomContextId) {
    this.store.dispatch(
      LokiActions.log({
        args: roomContextId ? [...args, { roomContextId }] : args
      })
    );
  }
}
